import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { copyToClipboard } from '@/lib/browser/clipboard';

import type { ButtonType, ComponentSize } from '../types';
import { hasText } from '../data-panel/dataPanelItemBase';
import { NgButton } from './NgButton';

const CONFIRMATION_DURATION_MS = 2000;

type CopyOutcome = 'idle' | 'copied' | 'failed';

type CopyAttempt = {
  value: string;
  outcome: Exclude<CopyOutcome, 'idle'>;
};

const OUTCOME_ICON: Record<CopyOutcome, string> = {
  idle: 'fal fa-copy',
  copied: 'fal fa-check-circle',
  failed: 'fal fa-warning',
};

const OUTCOME_MESSAGE_KEY: Record<CopyOutcome, string> = {
  idle: 'CopyButtonTitle',
  copied: 'CopyButtonConfirmationMessage',
  failed: 'CopyButtonFailureMessage',
};

export type NgCopyButtonProps = {
  cssClass?: string;
  type?: ButtonType;
  outline?: boolean;
  size?: ComponentSize;
  valueToCopy?: string;
  testID?: string;
};

export function NgCopyButton({
  cssClass,
  type = 'link',
  outline,
  size = 'small',
  valueToCopy,
  testID,
}: NgCopyButtonProps) {
  const { t } = useTranslation();
  const [attempt, setAttempt] = useState<CopyAttempt | null>(null);
  const attemptTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(attemptTimer.current), []);

  if (!hasText(valueToCopy)) {
    return null;
  }

  const copiedValue = valueToCopy.trim();
  const outcome: CopyOutcome = attempt?.value === copiedValue ? attempt.outcome : 'idle';

  const copyValueToClipboard = async () => {
    const succeeded = await copyToClipboard(copiedValue);

    setAttempt({ value: copiedValue, outcome: succeeded ? 'copied' : 'failed' });
    clearTimeout(attemptTimer.current);
    attemptTimer.current = setTimeout(() => setAttempt(null), CONFIRMATION_DURATION_MS);
  };

  const message = t(OUTCOME_MESSAGE_KEY[outcome]);

  return (
    <NgButton
      type={type}
      outline={outline}
      size={size}
      icon={OUTCOME_ICON[outcome]}
      cssClass={cssClass}
      title={outcome === 'idle' ? undefined : message}
      ariaLabel={message}
      testID={testID}
      onClick={copyValueToClipboard}
    />
  );
}
